<template>
  <div class="flex flex-col gap-y-6 relative" @click="closeAllFilters">
    <div class="flex flex-col md:flex-row justify-end md:gap-2 items-start md:items-center w-full">
      <div
        v-if="!isRequestsV2"
        class="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto sm:justify-between md:justify-end mb-1 sm:mb-0"
      >
        <p class="text-primary30 text-sm font-bold whitespace-nowrap">Ordenar por:</p>
        <div class="relative mb-1 sm:mb-0">
          <OutlinedButton
            :text="currentSortLabel"
            width="auto"
            height="32px"
            @click="isSortModalOpen = true"
            :prepend-icon="mdiChevronDown"
          />
          <SortOptionsModal
            :is-open="isSortModalOpen"
            :options="sortOptions"
            :selected-value="currentSort"
            @select="handleSortSelect"
            @update:is-open="isSortModalOpen = $event"
          />
        </div>
      </div>

      <div class="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
        <!--
          Apontamento 4 — botão de aprovação em lote. Só aparece quando a listagem traz
          container elegível (policy com `bulkApproveEnabled` e 100% conciliado): aprovar em
          lote alimenta pagamento, então o default é não existir.
        -->
        <!--
          F2/M3-2 — a contagem exclui `ATIVIDADE` (auditado um a um). Quando a página só
          tem Registro de Atividade, o botão aparece DESABILITADO com o motivo em vez de
          sumir: sumir faria o auditor procurar o botão que "desapareceu".
        -->
        <!--
          Aprovar em lote exige `attendance_basic_receiving` (rota `bulk-approve`): a fila
          também é lida por quem só envia e pelo perfil Extrator, que levariam 403.
        -->
        <!--
          Spec 2026-10-01 (ruling de segurança da Task 9) — no Kanban não há seleção: o lote
          mandaria cartões que o auditor não escolheu, inclusive fora da tela, e isso alimenta
          pagamento. O botão fica DESABILITADO com o caminho (Tabela + seleção) e um atalho.
        -->
        <div
          v-if="!isRequestsV2 && canAuditAttendance && isKanbanActive"
          class="flex items-center gap-2 shrink-0 w-full sm:w-auto"
          data-testid="ds-attendance-bulk-approve"
          :title="KANBAN_BULK_APPROVE_HINT"
        >
          <!--
            shrink-0 + largura própria: como item flex encolhível, o wrapper ficava mais
            estreito que o botão (width 100% de si mesmo) e o "Usar Tabela" vazava para baixo
            do "Exportar registro", sem clique em 1280 px (BUGS.md B-03, fase 7).
          -->
          <OutlinedButton
            text="Aprovar em lote"
            width="auto"
            class="w-full sm:w-auto sm:max-w-[220px]"
            height="32px"
            data-testid="ds-attendance-bulk-approve-button"
            :disabled="true"
          />
          <button
            type="button"
            class="whitespace-nowrap text-sm font-bold text-primary underline"
            data-testid="ds-attendance-bulk-approve-use-table"
            @click="handleViewChange('table')"
          >
            Usar Tabela
          </button>
        </div>
        <div
          v-else-if="
            !isRequestsV2 && canAuditAttendance && (bulkApproveIds.length > 0 || listHasActivity)
          "
          data-testid="ds-attendance-bulk-approve"
          :title="bulkApproveTitle"
        >
          <OutlinedButton
            :text="`Aprovar em lote (${bulkApproveIds.length})`"
            width="100%"
            class="w-full sm:w-auto sm:max-w-[220px]"
            height="32px"
            data-testid="ds-attendance-bulk-approve-button"
            :disabled="isBulkApproving || bulkApproveIds.length === 0"
            @click="isBulkApproveModalOpen = true"
          />
        </div>
        <!--
          Uma entrada só para as duas exportações: o modal já era um componente
          único com `mode`, e dois botões para ele só duplicavam a porta. A
          escolha virou aba lá dentro.

          F4/M3 ④ — anexo de Registro de Atividade sai por rota PRÓPRIA, sob
          `attendance_extracting`. Quem não tem a permissão não recebe a aba
          (ergonomia; a segurança é o 403 do backend): no D-0 só o Administrador
          Geral a tem, e um caminho que sempre falha treina a Operação a ignorá-lo.

          Perfil Extrator — a aba "Registros de plantão" segue `attendance_sheet_exporting`
          (receptor básico+ ou extrator), a permissão de `containers/export`. Sem nenhuma
          das duas exportações (emissor puro), o botão some.
        -->
        <OutlinedButton
          v-if="!isRequestsV2 && (canExportSheets || canExportAttachments)"
          data-testid="ds-attendance-export-button"
          text="Exportar registro"
          width="100%"
          class="w-full sm:w-auto sm:max-w-[200px]"
          height="32px"
          @click="openExportModal()"
          :prepend-icon="mdiTrayArrowDown"
        />
        <!--
          F2/M2 (balão ②) — "Novo registro" vira "Enviar registro ▾" com duas saídas. O
          dropdown só existe quando a escala/empresa habilita Registro de Atividade
          (`registration-settings`): sem ele, a tela é EXATAMENTE a de hoje — inclusive o
          `data-testid` antigo, que os e2e clicam direto.
        -->
        <template v-if="canOpenActivityRecord">
          <div class="relative w-full sm:w-auto lg:mr-5 md:mr-5">
            <OutlinedButton
              data-testid="ds-attendance-enviar-registro-dropdown"
              text="Enviar registro"
              width="100%"
              class="w-full sm:w-auto sm:max-w-[180px]"
              height="32px"
              :prepend-icon="mdiPlus"
              @click.stop="toggleSendRecordMenu"
            />
            <div
              v-if="isSendRecordMenuOpen"
              class="absolute right-0 z-20 mt-2 w-[330px] overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
              data-testid="ds-attendance-enviar-registro-menu"
              @click.stop
            >
              <button
                type="button"
                class="w-full px-3 py-2 text-left hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                data-testid="ds-attendance-novo-registro-button"
                :disabled="!canEmitSheetRecord || Boolean(sheetBlockedReason)"
                :title="sendRecordTooltip"
                @click="openSheetUpload"
              >
                <span class="block text-sm font-bold text-textPrimary">Registro de Plantão</span>
                <span
                  v-if="sheetBlockedReason"
                  class="block text-xs font-bold text-[#B45F09]"
                  data-testid="ds-attendance-novo-registro-blocked"
                >
                  {{ sheetBlockedReason }}
                </span>
                <span v-else class="block text-xs text-neutral50">
                  Folha diária · Folha não diária · App externo
                </span>
              </button>
              <button
                type="button"
                class="w-full border-t border-neutral-200 px-3 py-2 text-left hover:bg-gray-100"
                data-testid="ds-attendance-enviar-registro-atividade"
                @click="openActivityRecord"
              >
                <span class="block text-sm font-bold text-textPrimary">
                  Registro de Atividade · Jornada
                </span>
                <span class="block text-xs text-neutral50">
                  Anexos + equivalência declarada por perna
                </span>
              </button>
            </div>
          </div>
        </template>
        <OutlinedButton
          v-else
          data-testid="ds-attendance-novo-registro-button"
          text="Novo registro"
          width="100%"
          class="w-full sm:w-auto sm:max-w-[160px] lg:mr-5 md:mr-5"
          height="32px"
          :disabled="!canEmitSheetRecord"
          :title="sendRecordTooltip"
          @click="openSheetUpload"
          :prepend-icon="mdiPlus"
        />
      </div>
    </div>

    <!--
      F2/M3 (balão ①) — categoria acima dos filtros de hoje. "Todos" é o default: a fila
      continua sendo UM lugar (Q6). Sem `counts-by-kind` no backend, o controle nem aparece.
    -->
    <DsAttendanceKindTabs
      :model-value="kindFilter"
      :counts="tabCounts"
      :only-requests="onlyRequests"
      :panel-id="KIND_PANEL_ID"
      @update:only-requests="handleOnlyRequestsChange"
      @update:model-value="handleKindChange"
    />

    <div v-if="!isRequestsV2" class="w-full flex flex-col lg:flex-row items-center gap-2">
      <div class="flex flex-wrap w-full gap-2">
        <!--
          F3/I5 — na aba "Solicitações" o backend DESCARTA o `status`
          (`effectiveParams = params.onlyRequests ? { ...params, status: undefined } : params`).
          Deixar o dropdown clicável seria um filtro fantasma: a lista mostra
          `PENDING_SUBMISSION` sob o chip "Aguardando auditoria" e mexer nele não muda nada.
          Mesmo tratamento de "Tipo de Registro de Atividade" logo abaixo.
        -->
        <div v-if="showStatusFilter" class="relative">
          <div
            @click.stop="toggleFilter('status')"
            data-testid="ds-attendance-filtro-status-select"
          >
            <SelectFilter
              label="Status"
              :icon="statusIcon"
              :options="statusOptions"
              dropdown-width="w-40"
              @select="handleStatusSelect"
              :is-open="openFilterName === 'status'"
              multiple
              :selectedValue="selectedStatusKeys"
            />
          </div>
        </div>

        <!-- Spec 2026-10-01 §7.6 — "Estado do prazo": escondido no K1 (as colunas já são os estados). -->
        <div v-if="showSlaStateFilter" class="relative">
          <div
            @click.stop="toggleFilter('slaState')"
            data-testid="ds-attendance-filtro-estado-prazo-select"
          >
            <SelectFilter
              label="Estado do prazo"
              :icon="statusIcon"
              :options="slaStateOptions"
              dropdown-width="w-48"
              @select="handleSlaStateSelect"
              :is-open="openFilterName === 'slaState'"
              multiple
              :selectedValue="queueFilterState.slaStates"
            />
          </div>
        </div>

        <!-- "Etapa": escondida no K2 (as colunas já são as etapas). -->
        <div v-if="showStageFilter" class="relative">
          <div @click.stop="toggleFilter('stage')" data-testid="ds-attendance-filtro-etapa-select">
            <SelectFilter
              label="Etapa"
              :icon="statusIcon"
              :options="stageOptions"
              dropdown-width="w-56"
              @select="handleStageSelect"
              :is-open="openFilterName === 'stage'"
              multiple
              :selectedValue="queueFilterState.stages"
            />
          </div>
        </div>

        <!--
          Fase 2 do fluxo de auditoria (spec 2026-09-11, §6.3) — por que a máquina mandou o
          registro para gente. Multi-select porque a fila é trabalhada por tipo de problema
          ("hoje eu resolvo as fora de tolerância"), não por um motivo de cada vez.
        -->
        <div class="relative">
          <div
            @click.stop="toggleFilter('manualReviewReason')"
            data-testid="ds-attendance-filtro-motivo-revisao-select"
          >
            <SelectFilter
              label="Motivo da revisão"
              :icon="statusIcon"
              :options="manualReviewReasonOptions"
              dropdown-width="w-64"
              @select="handleManualReviewReasonSelect"
              :is-open="openFilterName === 'manualReviewReason'"
              multiple
              :selectedValue="selectedManualReviewReasons"
            />
          </div>
        </div>

        <!--
          F2/M3 — "Tipo de Registro de Atividade". Só faz sentido quando a Atividade está no
          recorte (aba "Todos" ou "Atividade"); na aba "Folha" o filtro sairia do ar sem
          nunca casar com nada.
        -->
        <div v-if="showActivityTypeFilter" class="relative">
          <div
            @click.stop="toggleFilter('activityType')"
            data-testid="ds-attendance-filtro-tipo-atividade-select"
          >
            <SelectFilter
              label="Tipo de Registro de Atividade"
              :icon="statusIcon"
              :options="activityTypeOptions"
              dropdown-width="w-72"
              @select="handleActivityTypeSelect"
              :is-open="openFilterName === 'activityType'"
              :selectedValue="selectedActivityType"
            />
          </div>
        </div>

        <div @click.stop="toggleFilter('date')">
          <DateFilter
            title="Data plantão"
            ref="dateFilterRef"
            :is-open="openFilterName === 'date'"
            @select="handleDateFilter"
          />
        </div>

        <!--
          Na aba "Solicitações" o filtro não existe: nada foi enviado ainda, então
          ele devolveria lista vazia e mandaria o usuário procurar um bug que não
          há. Mesma regra (e mesmo motivo) do filtro de tipo de atividade logo
          abaixo — o dado só nasce no ENVIO.
        -->
        <div v-if="showUploadDateFilter" @click.stop="toggleFilter('uploadDate')">
          <DateFilter
            ref="uploadDateFilterRef"
            title="Data upload"
            :is-range="false"
            :is-open="openFilterName === 'uploadDate'"
            @select="handleUploadDateFilter"
          />
        </div>

        <div class="relative">
          <div @click.stop="toggleFilter('unit')" data-testid="ds-attendance-filtro-unidade-select">
            <SelectFilter
              label="Unidades"
              :icon="hospitalIcon"
              :options="healthUnitOptions"
              searchable
              dropdown-width="w-64"
              @select="handleHealthUnitSelect"
              :is-open="openFilterName === 'unit'"
              multiple
              :selectedValue="selectedHealthUnitIds"
            />
          </div>
        </div>

        <div class="relative">
          <div @click.stop="toggleFilter('schedule')">
            <SelectFilter
              label="Escala"
              :icon="scheduleIcon"
              :options="scheduleOptions"
              searchable
              dropdown-width="w-64"
              @select="handleScheduleSelect"
              :is-open="openFilterName === 'schedule'"
              multiple
              :selectedValue="selectedScheduleIds"
              :disabled="!selectedHealthUnitIds.length"
            />
          </div>
        </div>

        <!--
          Apontamento 3 (vídeo 03/09) — o filtro `hasDivergentEntries` já existia no
          backend e não tinha UI: a fila de exceções era invisível na listagem. É um
          toggle, não um select: só faz sentido "somente com divergência" (o inverso
          diria ao backend "somente SEM divergência", que ninguém pediu).
        -->
        <!--
          `:model-value` + `@update:model-value` em vez de `v-model`:
          `toggleOnlyDivergent` também fecha os outros filtros e refaz a busca,
          efeitos que um v-model puro perderia.
        -->
        <DsSwitch
          :model-value="onlyDivergent"
          label="Somente com divergência"
          size="small"
          class="h-8 whitespace-nowrap"
          data-testid="ds-attendance-filtro-divergencia"
          @update:model-value="toggleOnlyDivergent()"
        />

        <!--
          Pacote 2026-09-10 — indicativo de pagamento por registro (backend #2750): filtros
          "Com Antecipe" / "Com À vista" no mesmo padrão do toggle de divergência.
        -->
        <DsSwitch
          :model-value="onlyAntecipe"
          label="Com Antecipe"
          size="small"
          class="h-8 whitespace-nowrap"
          data-testid="ds-attendance-filtro-antecipe"
          @update:model-value="toggleOnlyAntecipe()"
        />
        <DsSwitch
          :model-value="onlyInCash"
          label="Com À vista"
          size="small"
          class="h-8 whitespace-nowrap"
          data-testid="ds-attendance-filtro-a-vista"
          @update:model-value="toggleOnlyInCash()"
        />

        <!-- "Só vencidos": prazo da ETAPA ATUAL vencido — envio ou auditoria (G2-D9). -->
        <DsSwitch
          v-if="showOverdueFilter"
          :model-value="queueFilterState.onlyOverdue"
          label="Só vencidos"
          size="small"
          class="h-8 whitespace-nowrap"
          data-testid="ds-attendance-filtro-so-vencidos"
          @update:model-value="toggleOnlyOverdue()"
        />

        <!--
          Fila de revisão da auditoria: registros em "Auditoria em progresso" cuja
          vaga/plantão mudou depois do início da auditoria (o backend deriva a flag de
          station/doctor_station × locked_at). Toggle pelo mesmo motivo do de divergência.
        -->
        <DsSwitch
          :model-value="onlyStationChanged"
          label="Plantão alterado após auditoria"
          size="small"
          class="h-8 whitespace-nowrap"
          data-testid="ds-attendance-filtro-plantao-alterado"
          @update:model-value="toggleOnlyStationChanged()"
        />

        <!--
          Fase 1 do fluxo de auditoria (spec 2026-09-11) — o backend passou a esconder da
          fila (e do contador do resumo) os registros cuja escala não prevê plantão na data.
          Este toggle os traz de volta, para quem precisa conferir a marcação. Desligado por
          padrão e lembrado por usuário: é preferência de trabalho, não filtro da URL.
        -->
        <DsSwitch
          :model-value="showNoScheduleExpected"
          label="Mostrar sem escala prevista"
          size="small"
          class="h-8 whitespace-nowrap"
          data-testid="ds-attendance-filtro-sem-escala-prevista"
          @update:model-value="toggleShowNoScheduleExpected()"
        />

        <div class="relative">
          <div @click.stop="toggleFilter('shift')">
            <SelectFilter
              label="Turno"
              :icon="shiftIcon"
              :options="shiftOptions"
              searchable
              dropdown-width="w-64"
              @select="handleShiftSelect"
              :is-open="openFilterName === 'shift'"
              multiple
              :selectedValue="selectedShiftIds"
              :disabled="!selectedScheduleIds.length"
            />
          </div>
        </div>

        <div v-if="showRegistrationModeFilter" class="relative">
          <div
            @click.stop="toggleFilter('registrationMode')"
            data-testid="ds-attendance-filtro-modalidade-select"
          >
            <SelectFilter
              label="Modalidade de registro"
              :icon="statusIcon"
              :options="REGISTRATION_MODE_OPTIONS"
              dropdown-width="w-80"
              @select="handleRegistrationModeSelect"
              :is-open="openFilterName === 'registrationMode'"
              multiple
              :selectedValue="selectedRegistrationModes"
            />
          </div>
        </div>
      </div>
    </div>

    <!--
      Falha ao carregar os combos não pode virar dropdown vazio: era exatamente assim que o 403
      do receptor puro chegava à tela — "Nenhum resultado", indistinguível de "a empresa não tem
      unidades". Banner discreto, com nova tentativa, sem esconder a fila (que carrega à parte).
    -->
    <div
      v-if="filterOptionsLoadError"
      class="flex items-center justify-between gap-3 px-4 py-2 mb-2 text-sm border rounded-md border-amber-300 bg-amber-50 text-amber-900"
      role="status"
      data-testid="ds-attendance-erro-filtros"
    >
      <span>{{ filterOptionsLoadError }}</span>
      <button
        type="button"
        class="font-medium underline whitespace-nowrap"
        @click="reloadFilterOptions()"
      >
        Tentar novamente
      </button>
    </div>

    <ActiveFiltersTags
      v-if="!isRequestsV2"
      :filters="unifiedFiltersList"
      :show-clear-button="hasActiveFilters"
      @remove="handleRemoveGenericFilter"
      @clearAll="clearAllFilters"
    />

    <!--
      O painel que o segmented control recorta. `role="tabpanel"` só existe quando as abas
      existem (`counts !== null`): sem `tablist` na tela, um `tabpanel` solto atrapalha o
      leitor de tela em vez de ajudar.
    -->
    <div
      :id="KIND_PANEL_ID"
      class="flex flex-col min-h-[300px]"
      :role="kindCounts ? 'tabpanel' : undefined"
      :aria-labelledby="kindCounts ? activeKindTabId : undefined"
    >
      <ActivityRequestsTab
        v-if="isRequestsV2"
        :can-export="canExportSheets"
        @counters="onRequestsV2Counters"
      />
      <!--
        Spec 2026-10-01 §7.1 — Registro de plantão (Todos/Folha/Atividade): Kanban, Lista e
        Tabela. Daqui para baixo (skeleton, erro, cartões de hoje) é só a aba Solicitações
        antiga, intocada (CA9).
      -->
      <DsAttendanceQueueViews
        v-else-if="showQueueViews"
        :view="queueFilterState.view"
        :group-by="queueFilterState.groupBy"
        :list-rows="queueContainers"
        :kanban-columns="kanbanColumnDefs"
        :kanban-rows="kanban.rows.value"
        :kanban-totals="kanban.totals.value"
        :kanban-has-more="kanban.hasMore.value"
        :kanban-loading="kanban.loading.value"
        :kanban-initial-loading="kanban.initialLoading.value"
        :kanban-failed="kanban.failed.value"
        :now-ms="nowMs"
        :loading="loading"
        :error="containersLoadError"
        :empty-title="queueEmptyTitleText"
        :selected-ids="selectedTableIds"
        :deadline-sorted="currentSort === 'SLA'"
        :skeleton-count="itemsPerPage"
        @update:view="handleViewChange"
        @update:group-by="handleGroupByChange"
        @update:selected-ids="selectedTableIds = $event"
        @load-more="kanban.loadMore"
        @details="handleDetails"
        @retry="fetchData()"
        @sort-by-deadline="handleSortByDeadline"
        @bulk-approve="isBulkApproveModalOpen = true"
      />
      <template v-else-if="loading">
        <AuditoriumExceptionalitiesCardSkeleton v-for="n in itemsPerPage" :key="`skeleton-${n}`" />
      </template>

      <!-- Falha de carregamento não pode virar "Nenhum registro encontrado": o estado
           de erro traz a mensagem específica do service e o botão de nova tentativa. -->
      <ReportErrorState
        v-else-if="containersLoadError"
        title="Não foi possível carregar os registros de presença"
        :message="containersLoadError"
        @retry="fetchData()"
      />

      <template v-else>
        <template v-if="list?.length">
          <DsAttendanceListCard
            v-for="(item, i) in list"
            :key="i"
            :item="item"
            @details="handleDetails"
          />
        </template>
        <EmptyList v-else title="Nenhum registro encontrado" />
      </template>
    </div>

    <v-pagination
      v-if="!isRequestsV2 && !isKanbanActive && !loading && !containersLoadError && list.length > 0"
      v-model="currentPage"
      :total-visible="5"
      :length="pageCount"
      density="comfortable"
      color="primary"
      @update:model-value="changePage"
    />
  </div>

  <AttendanceUploadModal
    v-model="isAttendanceUploadModalOpen"
    :initial-form="sheetInitialForm"
    :locked-fields="sheetLockedFields"
    @save="handleOnSave"
  />

  <!--
    F2/M2 — "Registro de Atividade · Jornada". A categoria já veio escolhida no dropdown, e
    `redirect-to-sheet` cobre o caminho de volta (o passo 1 do modal também bifurca).
    `v-if` no contexto: sem vaga de origem o modal não tem o que enviar (I7).
  -->
  <SendRecordModal
    v-if="sendRecordContext"
    v-model="isSendRecordModalOpen"
    :context="sendRecordContext"
    @redirect-to-sheet="handleRedirectToSheet"
    @saved="handleOnSave"
  />

  <DsAttendanceBulkApproveModal
    v-model="isBulkApproveModalOpen"
    :saving="isBulkApproving"
    :count="bulkApproveIds.length"
    :capped-out="bulkApproveCappedOut"
    @confirm="executeBulkApprove"
  />
  <DsAttendanceBulkApproveResultModal
    v-model="isBulkOutcomeModalOpen"
    :rows="bulkOutcomeRows"
    :subtitles="bulkOutcomeSubtitles"
    @open="openBulkOutcomeContainer"
  />

  <ExportDSAttendances
    v-model:is-open="isExportModalOpen"
    :mode="exportMode"
    :can-export-records="canExportSheets"
    :can-export-attachments="canExportAttachments"
    :active-filters="exportActiveFilters"
    :queue-filters="exportQueueFilters"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, defineAsyncComponent, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { useCanExportSheets } from './composables/useCanExportSheets'
import { useCanEmitAttendance } from './composables/useCanEmitAttendance'
import { useCanAuditAttendance } from './composables/useCanAuditAttendance'
import {
  queueEmptyTitle,
  useDsAttendanceQueueFilters
} from './composables/useDsAttendanceQueueFilters'
import type {
  DsAttendanceGroupBy,
  DsAttendanceQueueBaseParams
} from './composables/useDsAttendanceQueueFilters'
import { useDsAttendanceKanbanColumns } from './composables/useDsAttendanceKanbanColumns'
import DsAttendanceQueueViews from './components/queue/DsAttendanceQueueViews.vue'
import { dsAttendanceKanbanColumns } from './utils/queue-kanban-columns'
import DsAttendanceBulkApproveModal from './components/DsAttendanceBulkApproveModal.vue'
import DsAttendanceBulkApproveResultModal from './components/DsAttendanceBulkApproveResultModal.vue'
import {
  ACTIVITY_NOT_BULK_APPROVABLE_REASON,
  bulkApproveResultMessage,
  isActivityContainer,
  isBulkApproveEligible,
  selectBulkApproveIds,
  buildBulkApproveOutcomeRows,
  buildBulkOutcomeSubtitle,
  type BulkApproveOutcomeRow
} from './utils/bulk-approve'
import {
  readShowNoScheduleExpected,
  writeShowNoScheduleExpected
} from './utils/no-schedule-expected-preference'

import EmptyList from '@/components/EmptyList.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import ActiveFiltersTags, { FilterTag } from '@/components/Filters/ActiveFilters.vue'
import AuditoriumExceptionalitiesCardSkeleton from '@/components/designSystem/Skeleton/AuditoriumExceptionalitiesCardSkeleton.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import DsAttendanceListCard from './components/DsAttendanceListCard.vue'
import DsAttendanceKindTabs from './components/DsAttendanceKindTabs.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import AttendanceUploadModal from '@/components/designSystem/modals/AttendanceUploadModal/Index.vue'
// Modal de 5 passos da W4 (o STUB que a W5 criou neste caminho foi substituído no merge).
import SendRecordModal from '@/components/designSystem/modals/SendRecordModal/Index.vue'
import {
  buildSheetHandoff,
  isSheetCategoryBlocked,
  SHEET_BLOCKED_BY_APP_POLICY_REASON
} from '@/components/designSystem/modals/SendRecordModal/types'
import type {
  RedirectToSheetPayload,
  SendRecordContext
} from '@/components/designSystem/modals/SendRecordModal/types'
import type { DsAttendanceUploadForm } from '@/components/designSystem/modals/AttendanceUploadModal/types'

import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toDateOnly } from '@/utils/date'
import { storeToRefs } from 'pinia'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import { mdiChevronDown, mdiPlus, mdiTrayArrowDown } from '@mdi/js'
import ExportDSAttendances from '@/components/designSystem/modals/ExportDSAttendances.vue'
import SortOptionsModal from '@/components/Filters/SortOptionsModal.vue'
import type { ModuleViewCounters, QueueViewMode } from '@/components/designSystem/QueueViews/types'
import { useServerNowTick } from '@/components/designSystem/QueueViews/useServerNowTick'
import ActivityRequestsTab from './components/requests/ActivityRequestsTab.vue'
import { REGISTRATION_MODE_OPTIONS } from './utils/activity-request-filter-options'
import {
  REGISTRATION_MODES,
  REGISTRATION_MODE_LABELS
} from '@/constants/activity-requests.constants'
import type { RegistrationMode } from '@/types/activity-requests.types'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  ACTIVITY_TYPE_LABELS,
  ActivityType,
  ContainerKind,
  defaultContainerOrderBy,
  DS_ATTENDANCE_STATUS_OPTIONS,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import {
  CONTAINER_SLA_FILTER_STATES,
  CONTAINER_SLA_FILTER_STATE_LABELS,
  CONTAINER_STAGES,
  CONTAINER_STAGE_LABELS
} from '@/constants/ds-attendance-sla.constants'
import type {
  ContainerSlaFilterState,
  ContainerStage
} from '@/constants/ds-attendance-sla.constants'
import { MANUAL_REVIEW_REASON_OPTIONS } from '@/constants/ds-attendance-auto-approval.constants'
import { pathForStatus, routeNameForStatus } from './utils/status-route-map'
import { buildExportFilterChips } from './utils/export-filter-chips'
import { toAttendanceListItem, type AttendanceListItem } from './utils/attendance-list-item'
import type {
  DSAttendanceContainerOrderBy,
  ListContainersByCompanyParams
} from '@/types/ds-attendance.types'

const dsAttendanceStore = useDsAttendanceStore()
const permissionsStore = usePermissionsStore()
/** Mensagem de falha da última busca de registros — `null` quando a lista carregou. */
const containersLoadError = computed(() => dsAttendanceStore.containersLoadError)

interface DateRange {
  start: Date | null
  end: Date | null
}

interface SortOption {
  label: string
  value: string
  direction: 'asc' | 'desc'
  group: string
}

/**
 * Spec 2026-10-01 §7.2 — a fila abre pelo PRAZO (Vencida → Atenção → No prazo → Sem SLA →
 * Encerrado); "Prioridade de pagamento" (pacote 2026-09-10) continua como opção e segue
 * padrão na aba Solicitações (F17/CA9). O mesmo helper alimenta o "próximo" (`useNextContainer`):
 * a lista só grava `sort` na URL quando ele difere deste padrão. A tela sempre manda o `orderBy`.
 */
const defaultSort = computed(() => defaultContainerOrderBy(onlyRequests.value))

const sortOptionsData: SortOption[] = [
  { label: 'Prazo (SLA)', value: 'SLA', direction: 'asc', group: 'payment' },
  {
    label: 'Prioridade de pagamento',
    value: 'PAYMENT_PRIORITY',
    direction: 'desc',
    group: 'payment'
  },
  /**
   * Eixo 4 da discovery 2026-09-11 — score de risco do backend (divergência aberta, plantão
   * alterado após auditoria, idade). Mediana de 1,4 dias contra p90 de 5,8: ordenar por risco
   * é o que tira do fim da fila o registro que ninguém pega.
   */
  { label: 'Risco', value: 'RISK', direction: 'desc', group: 'payment' },
  { label: 'Status', value: 'STATUS', direction: 'asc', group: 'created' },
  { label: 'Data de criação crescente', value: 'CREATED_ASC', direction: 'asc', group: 'created' },
  {
    label: 'Data de criação decrescente',
    value: 'CREATED_DESC',
    direction: 'desc',
    group: 'created'
  }
]

const router = useRouter()
const { flags } = useFeatureFlags()
const route = useRoute()
const { notifyError } = useErrorToast()

const currentSort = ref<string>(defaultContainerOrderBy(false))
const isSortModalOpen = ref(false)
const loading = ref(true)
const list = ref<AttendanceListItem[]>([])

const openFilterName = ref<string | null>(null)
const dateFilterRef = ref<InstanceType<typeof DateFilter> | null>(null)
const uploadDateFilterRef = ref<InstanceType<typeof DateFilter> | null>(null)
const isAttendanceUploadModalOpen = ref(false)

/* --------------------------------------------------------------------------- *
 * F2 — categoria da fila, filtro de tipo e o dropdown "Enviar registro".
 * --------------------------------------------------------------------------- */

/** `null` = "Todos". Array para acompanhar o `kind` repetível do contrato de listagem. */
const kindFilter = ref<ContainerKind[] | null>(null)
/**
 * F3 — aba "Solicitações". Recorte próprio (`onlyRequests` do backend, §3.1) e exclusivo com
 * o `kind`: enquanto a política não cobrar nada, a aba nem existe e isto fica `false`.
 */
const onlyRequests = ref(false)

/**
 * Spec 2026-09-25 §6.7 — com a flag, "Solicitações" é a aba NOVA (endpoints próprios,
 * `ActivityRequestsTab`). Sem ela, a aba velha fica exatamente como está.
 */
const isRequestsV2 = computed(() => flags.enabledActivityRequestsV2 && onlyRequests.value)

/** `counters.byView.ALL` da aba nova — o número da pílula "Solicitações" (§6.1). */
const requestsV2Total = ref<number | null>(null)

function onRequestsV2Counters(counters: ModuleViewCounters): void {
  requestsV2Total.value = counters.byView?.ALL ?? null
}

watch(onlyRequests, (value) => {
  if (!value) requestsV2Total.value = null
})
const selectedActivityType = ref<ActivityType | null>(null)
const isSendRecordMenuOpen = ref(false)
const isSendRecordModalOpen = ref(false)

/** Contagem por espécie do store (`null` enquanto o backend não serve a rota). */
const kindCounts = computed(() => dsAttendanceStore.counts)

/** Com a aba nova ativa, a pílula "Solicitações" conta pela fonte nova; as outras não mudam. */
const tabCounts = computed(() =>
  kindCounts.value && requestsV2Total.value !== null
    ? { ...kindCounts.value, requests: requestsV2Total.value }
    : kindCounts.value
)

/**
 * `id` do painel recortado pelas abas de categoria. Fixo (a fila é única na rota) e
 * compartilhado entre o `aria-controls` das abas e o `id` do container da lista — sem um
 * id estável aqui, as abas ficavam sem `aria-controls` (pendência I17 da WF2).
 */
const KIND_PANEL_ID = 'ds-attendance-queue-list'

/** Aba que rotula o painel — o par `aria-labelledby` do `aria-controls`. */
const activeKindTabId = computed(() => {
  // A aba "Solicitações" (F3) é um recorte próprio, fora do `kind`: sem este ramo o painel
  // continuaria rotulado por "Todos" enquanto a cobrança estivesse aberta.
  if (onlyRequests.value) return 'ds-attendance-kind-tab-requests'
  const kind = kindFilter.value?.[0]
  if (kind === ContainerKind.FOLHA) return 'ds-attendance-kind-tab-folha'
  if (kind === ContainerKind.ATIVIDADE) return 'ds-attendance-kind-tab-atividade'
  return 'ds-attendance-kind-tab-all'
})

/** A política/flag global libera o Registro de Atividade? Default seguro: não. */
const canSendActivityRecord = computed(() => dsAttendanceStore.canSendActivityRecord)

const activityTypeOptions = Object.values(ActivityType).map((type) => ({
  label: ACTIVITY_TYPE_LABELS[type],
  value: type as string
}))

/**
 * Só existe quando a F2 está no ar (é `counts-by-kind` que prova isso) e a Atividade está no
 * recorte: na aba "Folha" o filtro não casaria com nada, e em backend antigo ele seria um
 * filtro que o servidor ignora.
 */
/** I5 — o `status` não existe como recorte na aba "Solicitações": o backend o ignora. */
const showStatusFilter = computed(() => !onlyRequests.value)

/**
 * "Data upload" filtra o upload REAL da evidência (`uploaded_at`). Na aba
 * "Solicitações" não há upload nenhum — a solicitação é justamente o registro
 * que ainda não chegou —, então o filtro só produziria lista vazia.
 */
const showUploadDateFilter = computed(() => !onlyRequests.value)

const showActivityTypeFilter = computed(
  () =>
    kindCounts.value !== null &&
    // Na aba "Solicitações" não há o que filtrar por tipo: o tipo só nasce no ENVIO, e
    // uma solicitação é justamente o registro que ainda não chegou.
    !onlyRequests.value &&
    (kindFilter.value === null || kindFilter.value.includes(ContainerKind.ATIVIDADE))
)

/**
 * O segmented control emite SEMPRE os dois eventos, nesta ordem: `update:onlyRequests` e
 * depois `update:modelValue`. Por isso este handler só guarda o recorte — quem fecha os
 * filtros e refaz a busca é o `handleKindChange` logo em seguida, e a fila busca uma vez só.
 */
function handleOnlyRequestsChange(value: boolean) {
  // Quem está no padrão acompanha o padrão da aba (SLA ⇄ Prioridade de pagamento, F17);
  // uma ordenação escolhida à mão viaja junto.
  const followsDefaultSort = currentSort.value === defaultSort.value
  onlyRequests.value = value
  // …exceto "Prazo (SLA)", que não existe em Solicitações (CA9).
  if (followsDefaultSort || !isSortAvailable(currentSort.value))
    currentSort.value = defaultSort.value
}

function handleKindChange(value: ContainerKind[] | null) {
  kindFilter.value = value && value.length ? value : null
  // Trocar para uma aba sem Atividade limpa o filtro de tipo junto (mesma regra do store).
  if (!showActivityTypeFilter.value) selectedActivityType.value = null
  // Idem para a data de upload ao entrar em "Solicitações": um filtro aplicado e
  // invisível zeraria a lista sem o usuário ter como descobrir por quê.
  if (!showUploadDateFilter.value) clearUploadDate()
  closeAllFilters()
  fetchData(true)
}

function handleActivityTypeSelect(value: string) {
  selectedActivityType.value = selectedActivityType.value === value ? null : (value as ActivityType)
  currentPage.value = 1
  fetchData(true)
}

function toggleSendRecordMenu() {
  isSendRecordMenuOpen.value = !isSendRecordMenuOpen.value
}

/**
 * Semente do wizard de folha. `null` = entrada direta pelo menu, tudo editável
 * (o comportamento de hoje); preenchida, vem do reencaminhamento de M2 (§8.2).
 */
const sheetInitialForm = ref<Partial<DsAttendanceUploadForm> | null>(null)
const sheetLockedFields = ref<string[]>([])

function openSheetUpload() {
  isSendRecordMenuOpen.value = false
  // Guarda de verdade, não só o `:disabled` do botão: os três pontos de entrada
  // desta tela chamam esta função, e o wizard não checa permissão em passo algum.
  if (!canEmitSheetRecord.value || sheetBlockedReason.value) return
  sheetInitialForm.value = null
  sheetLockedFields.value = []
  isAttendanceUploadModalOpen.value = true
}

function openActivityRecord() {
  isSendRecordMenuOpen.value = false
  isSendRecordModalOpen.value = true
}

/**
 * Contexto de abertura pela fila.
 *
 * O objeto `{ origin, category, healthUnitIds, scheduleIds }` desta linha foi
 * escrito contra o STUB do `SendRecordModal` (W5) e nenhum daqueles campos
 * existe no `SendRecordContext` real da W4 — o modal os ignorava por completo.
 * Tipado, ele fica honesto: a fila não conhece a vaga de origem, e o
 * `doctorStationId`/`scheduleId` obrigatórios do contexto só nascem de um
 * seletor de plantão que ainda não existe (ponto de alinhamento com o B17, §8.2).
 *
 * Enquanto isso, `null` — o mesmo comportamento de hoje, sem o objeto morto.
 * O caminho vivo da M2 é o FAB do Detalhes do Plantão, que monta o contexto
 * completo.
 */
const sendRecordContext = computed<SendRecordContext | null>(() => null)

/**
 * I7 — sem contexto de vaga o modal abre com `doctorStationId = 0` e o passo 2
 * fica sem saída. Enquanto o seletor de escala/data não existir (§8.2, B17), a
 * fila não oferece o Registro de Atividade: o botão volta a ser o "Novo
 * registro" de hoje, exatamente como sem a feature.
 */
const canOpenActivityRecord = computed(
  () => canSendActivityRecord.value && sendRecordContext.value !== null
)

/**
 * O passo 1 do modal também bifurca: voltar para a folha reabre o wizard de
 * hoje — agora COM os campos da vaga preenchidos e bloqueados (C6/§8.2).
 */
function handleRedirectToSheet(payload: RedirectToSheetPayload) {
  const handoff = buildSheetHandoff(payload)
  sheetInitialForm.value = handoff.form
  sheetLockedFields.value = handoff.lockedFields
  isSendRecordModalOpen.value = false
  isAttendanceUploadModalOpen.value = true
}

const selectedStatusKeys = ref<DSAttendanceStatus[]>([])
const selectedScheduleIds = ref<string[]>([])
const selectedHealthUnitIds = ref<string[]>([])
const selectedShiftIds = ref<string[]>([])

/** Apontamento 3 — filtro server-side `hasDivergentEntries`, agora com UI. */
const onlyDivergent = ref(false)

function toggleOnlyDivergent() {
  onlyDivergent.value = !onlyDivergent.value
  closeAllFilters()
  fetchData(true)
}

/** Pacote 2026-09-10 — filtros de pagamento (`hasAntecipe` / `hasInCash` do backend #2750). */
const onlyAntecipe = ref(false)
const onlyInCash = ref(false)

function toggleOnlyAntecipe() {
  onlyAntecipe.value = !onlyAntecipe.value
  closeAllFilters()
  fetchData(true)
}

function toggleOnlyInCash() {
  onlyInCash.value = !onlyInCash.value
  closeAllFilters()
  fetchData(true)
}

/** Filtro server-side `stationChangedAfterAudit` — fila de revisão do time de auditoria. */
const onlyStationChanged = ref(false)

function toggleOnlyStationChanged() {
  onlyStationChanged.value = !onlyStationChanged.value
  closeAllFilters()
  fetchData(true)
}

/**
 * Fase 2 (§6.3) — filtro "Motivo da revisão". Vai para a URL: ao contrário do toggle de
 * "sem escala prevista", é filtro de negócio e um auditor manda o link para o outro.
 */
const selectedManualReviewReasons = ref<string[]>([])
const manualReviewReasonOptions = MANUAL_REVIEW_REASON_OPTIONS

function handleManualReviewReasonSelect(value: string) {
  const current = [...selectedManualReviewReasons.value]
  const index = current.indexOf(value)
  if (index > -1) current.splice(index, 1)
  else current.push(value)
  selectedManualReviewReasons.value = current
  currentPage.value = 1
  fetchData(true)
}

/** Acréscimo do dono (25/09) — "Modalidade de registro"; só existe com a flag do PR7. */
const selectedRegistrationModes = ref<RegistrationMode[]>([])
const showRegistrationModeFilter = computed(() => flags.enabledActivityRequestsV2)

function handleRegistrationModeSelect(value: string) {
  const mode = value as RegistrationMode
  selectedRegistrationModes.value = selectedRegistrationModes.value.includes(mode)
    ? selectedRegistrationModes.value.filter((m) => m !== mode)
    : [...selectedRegistrationModes.value, mode]
  currentPage.value = 1
  fetchData(true)
}

/**
 * Fase 1 do fluxo de auditoria — `includeNoScheduleExpected`. Não entra na URL (ao contrário
 * dos outros toggles): é preferência do auditor, persistida por usuário no `localStorage`.
 */
const showNoScheduleExpected = ref(readShowNoScheduleExpected())

function toggleShowNoScheduleExpected() {
  showNoScheduleExpected.value = !showNoScheduleExpected.value
  writeShowNoScheduleExpected(showNoScheduleExpected.value)
  closeAllFilters()
  fetchData(true)
}

/* --------------------------------------------------------------------------- *
 * Spec 2026-10-01 §7.5/§7.6 — recorte SLA (Estado do prazo, Etapa, Só vencidos) e visão.
 * O composable é a fonte; aqui só a ligação com a barra e a URL. Na aba Solicitações (flag
 * desligada) nada disso existe.
 * --------------------------------------------------------------------------- */
const queueFilters = useDsAttendanceQueueFilters()
const queueFilterState = queueFilters.filters
const showSlaStateFilter = computed(
  () => !onlyRequests.value && queueFilters.showSlaStateFilter.value
)
const showStageFilter = computed(() => !onlyRequests.value && queueFilters.showStageFilter.value)
const showOverdueFilter = computed(() => !onlyRequests.value)
const slaStateOptions = CONTAINER_SLA_FILTER_STATES.map((value) => ({
  label: CONTAINER_SLA_FILTER_STATE_LABELS[value],
  value
}))
const stageOptions = CONTAINER_STAGES.map((value) => ({
  label: CONTAINER_STAGE_LABELS[value],
  value
}))

function handleSlaStateSelect(value: string) {
  queueFilters.toggleSlaState(value as ContainerSlaFilterState)
  fetchData(true)
}

function handleStageSelect(value: string) {
  queueFilters.toggleStage(value as ContainerStage)
  fetchData(true)
}

function toggleOnlyOverdue() {
  queueFilters.update({ onlyOverdue: !queueFilterState.value.onlyOverdue })
  closeAllFilters()
  fetchData(true)
}

/** Chips do recorte SLA — separados para manter `unifiedFiltersList` abaixo do teto de complexidade. */
function queueSlaFilterTags(): FilterTag[] {
  const tags: FilterTag[] = []
  if (showSlaStateFilter.value) {
    queueFilterState.value.slaStates.forEach((state) =>
      tags.push({
        id: `slaState-${state}`,
        label: `Estado do prazo: ${CONTAINER_SLA_FILTER_STATE_LABELS[state]}`,
        type: 'slaState',
        value: state
      })
    )
  }
  if (showStageFilter.value) {
    queueFilterState.value.stages.forEach((stage) =>
      tags.push({
        id: `stage-${stage}`,
        label: `Etapa: ${CONTAINER_STAGE_LABELS[stage]}`,
        type: 'stage',
        value: stage
      })
    )
  }
  if (showOverdueFilter.value && queueFilterState.value.onlyOverdue) {
    tags.push({ id: 'overdue', label: 'Só vencidos', type: 'overdue' })
  }
  return tags
}

function removeQueueSlaFilter(filter: FilterTag): boolean {
  if (filter.type === 'slaState')
    queueFilters.toggleSlaState(filter.value as ContainerSlaFilterState)
  else if (filter.type === 'stage') queueFilters.toggleStage(filter.value as ContainerStage)
  else if (filter.type === 'overdue') queueFilters.update({ onlyOverdue: false })
  else return false
  fetchData(true)
  return true
}

/* --------------------------------------------------------------------------- *
 * Spec 2026-10-01 §7.1/§7.2 — as três visões. O `Index.vue` só liga: dados, carga por coluna
 * e regras moram em `DsAttendanceQueueViews` e nos composables. A aba Solicitações (antiga e
 * v2) não passa por aqui (CA9).
 * --------------------------------------------------------------------------- */
const showQueueViews = computed(() => !onlyRequests.value)
const isKanbanActive = computed(
  () => showQueueViews.value && queueFilterState.value.view === 'kanban'
)
/** Recorte da última busca: é ele que o Kanban carrega por coluna. */
const queueParams = ref<DsAttendanceQueueBaseParams | null>(null)
const kanbanReloadToken = ref(0)
const kanbanColumnDefs = computed(() =>
  dsAttendanceKanbanColumns(queueFilterState.value.groupBy, { kind: kindFilter.value?.[0] })
)
const kanban = useDsAttendanceKanbanColumns({
  columns: kanbanColumnDefs,
  isActive: isKanbanActive,
  baseParams: queueParams,
  reloadToken: kanbanReloadToken,
  onLoadMoreError: (error) =>
    notifyError(error, 'Não foi possível carregar mais registros desta coluna.')
})
/**
 * Um relógio por tela (G2-D13), na hora do SERVIDOR (F5): o `serverNow` da visão ativa — o do
 * Kanban (contadores/colunas) ou o da listagem paginada — e o outro só como reserva, para um
 * valor velho da visão anterior não vencer o da busca atual.
 */
const { nowMs } = useServerNowTick(() =>
  isKanbanActive.value
    ? (kanban.serverNow.value ?? dsAttendanceStore.serverNow ?? null)
    : (dsAttendanceStore.serverNow ?? kanban.serverNow.value ?? null)
)
/**
 * `ctx.nowMs` do recorte (o `closedSince` do Encerrado): relógio do servidor; o do cliente só
 * antes da 1ª resposta. Omitir o `closedSince` nessa 1ª busca mudaria o recorte (Encerrado com
 * TODO o histórico), e o desvio do relógio do cliente cai numa janela de 7 dias no piso da hora —
 * a 1ª resposta traz o `serverNow` e as buscas seguintes já usam a hora do servidor.
 */
const queueNowMs = (): number => nowMs.value ?? Date.now()

/** Seleção da Tabela — vale para a página/recorte em que foi feita (Review Focus 5). */
const selectedTableIds = ref<number[]>([])
const queueContainers = computed(() => dsAttendanceStore.containers)
const queueEmptyTitleText = computed(() =>
  queueEmptyTitle({
    onlyOverdue: queueFilterState.value.onlyOverdue,
    hasActiveFilters: hasActiveFilters.value
  })
)

function handleViewChange(view: QueueViewMode) {
  queueFilters.update({ view })
  closeAllFilters()
  fetchData(true)
}

function handleGroupByChange(groupBy: DsAttendanceGroupBy) {
  queueFilters.update({ groupBy })
  fetchData(true)
}

/** "Prazo" da Tabela ordena pelo SLA do servidor (rank + restante, §5.3). */
function handleSortByDeadline() {
  if (currentSort.value === 'SLA') return
  currentSort.value = 'SLA'
  fetchData(true)
}

/** §7.2 — sem polling: a tela recarrega ao voltar o foco depois de ≥ 60 s. */
const FOCUS_REFRESH_MS = 60_000
let lastFetchAt = 0
function onWindowFocus() {
  // Antes da 1ª busca do mount não há o que atualizar (evita busca duplicada).
  if (lastFetchAt === 0) return
  if (Date.now() - lastFetchAt >= FOCUS_REFRESH_MS) fetchData()
}
onUnmounted(() => window.removeEventListener('focus', onWindowFocus))

// --- Apontamento 4: aprovação em lote ---
const isBulkApproveModalOpen = ref(false)
const isBulkApproving = ref(false)
const toast = useToast()

const KANBAN_BULK_APPROVE_HINT = 'Para aprovar em lote, use a visão Tabela e selecione os registros'

/**
 * Candidatos do lote por visão (Q7/CA7): Tabela = só o que foi SELECIONADO; Kanban = nenhum
 * (o lote é desabilitado lá); Lista e aba Solicitações antiga = a página, como hoje. O corte de
 * elegibilidade (`isBulkApproveEligible`) vem depois, em `selectBulkApproveIds`: selecionado
 * e não elegível nunca vai no payload (Review Focus 5).
 */
const bulkCandidates = computed<AttendanceListItem[]>(() => {
  if (!showQueueViews.value) return list.value
  const view = queueFilterState.value.view
  if (view === 'table') {
    const selected = new Set(selectedTableIds.value)
    return list.value.filter((item) => selected.has(item.attendanceId))
  }
  // Kanban: sem seleção, sem lote (ruling de segurança) — ver `KANBAN_BULK_APPROVE_HINT`.
  if (view === 'kanban') return []
  return list.value
})

/** Ids que o lote vai enviar, já cortados no teto de 50 do backend. */
const bulkApproveIds = computed(() => selectBulkApproveIds(bulkCandidates.value))

/** Elegíveis que sobraram do teto — a confirmação avisa que ficam para o próximo lote. */
const bulkApproveCappedOut = computed(
  () => bulkCandidates.value.filter(isBulkApproveEligible).length - bulkApproveIds.value.length
)

/** F2/M3-2 — a página tem Registro de Atividade? É o que justifica o botão desabilitado. */
const listHasActivity = computed(() => bulkCandidates.value.some(isActivityContainer))

/**
 * O tooltip explica as duas situações: nenhum elegível (só Atividade na página) e a
 * contagem menor do que a lista (Atividade fora do lote).
 */
const bulkApproveTitle = computed(() => {
  if (!listHasActivity.value) return ''
  return ACTIVITY_NOT_BULK_APPROVABLE_REASON
})

// Spec 2026-09-24 §8.2 — quem não entrou no lote, e por quê.
const isBulkOutcomeModalOpen = ref(false)
const bulkOutcomeRows = ref<BulkApproveOutcomeRow[]>([])
const bulkOutcomeSubtitles = ref<Record<number, string>>({})

async function executeBulkApprove() {
  if (isBulkApproving.value || isKanbanActive.value || bulkApproveIds.value.length === 0) return

  isBulkApproving.value = true
  try {
    const result = await Network.dsAttendance.bulkApproveContainers({
      containerIds: bulkApproveIds.value
    })
    isBulkApproveModalOpen.value = false
    // Lote parcial é o caso comum (algum container mudou de estado no meio, ou o plantão
    // exige confirmar a sobrescrita): o toast conta, o modal de resultado explica.
    const failed = result.failed ?? []
    const skipped = result.skipped ?? []
    const notApproved = [...failed, ...skipped]
    const message = bulkApproveResultMessage(result.approved ?? [], notApproved)
    if (notApproved.length > 0) {
      toast.warning(message)
      bulkOutcomeSubtitles.value = Object.fromEntries(
        bulkCandidates.value.map((item) => [item.attendanceId, buildBulkOutcomeSubtitle(item)])
      )
      bulkOutcomeRows.value = buildBulkApproveOutcomeRows({ failed, skipped })
      isBulkOutcomeModalOpen.value = true
    } else {
      toast.success(message)
    }
    await fetchData()
  } catch (error) {
    notifyError(error, 'Não foi possível aprovar os registros em lote.')
  } finally {
    isBulkApproving.value = false
  }
}

/**
 * Item por id, procurado PRIMEIRO na fonte da visão ativa: no Kanban a `list` paginada não é
 * rebuscada e guarda o status de antes (rota errada); fora dele, os cartões é que estão velhos.
 */
function findQueueItem(id: number | string): AttendanceListItem | undefined {
  const fromList = () => list.value.find((item) => item.attendanceId === Number(id))
  const fromKanban = () => {
    const row = kanban.rows.value.find((candidate) => candidate.id === Number(id))
    return row ? toAttendanceListItem(row) : undefined
  }
  return isKanbanActive.value ? (fromKanban() ?? fromList()) : (fromList() ?? fromKanban())
}

/** Nova aba: o resultado do lote continua aberto para os próximos da lista. */
function openBulkOutcomeContainer(containerId: number) {
  const status = findQueueItem(containerId)?.status
  window.open(pathForStatus(containerId, status), '_blank', 'noopener,noreferrer')
}

const selectedDateRange = ref<DateRange>({ start: null, end: null })
const selectedDateLabel = ref<string | null>(null)

const selectedUploadDateRange = ref<DateRange>({ start: null, end: null })
const selectedUploadDateLabel = ref<string | null>(null)

const currentPage = ref(1)
const totalItems = ref(0)
const itemsPerPage = 10
const pageCount = computed(() => Math.ceil(totalItems.value / itemsPerPage) || 1)

// Os combos Unidades/Escala vêm do PRÓPRIO ds-attendance, e não de `useHealthUnitStore`/
// `useScheduleStore`. Dois motivos: (1) aqueles endpoints (`/healthUnits/list-view`,
// `GET /schedules`) exigem permissão de outros módulos e respondem 403 ao receptor puro do
// ds_attendance, que a store engolia devolvendo `[]` — combo "Nenhum resultado" sem aviso;
// (2) `listHealthUnits`/`listSchedules` são estado global sobrescrito por outras telas
// (FM-HEALTH-UNIT-LISTING-001). A fila passa a ser dona do que ela mesma oferece.
//
// Leitura por `computed` e não por `storeToRefs`, como o `containersLoadError` acima.
const filterHealthUnits = computed(() => dsAttendanceStore.filterHealthUnits)
const filterSchedules = computed(() => dsAttendanceStore.filterSchedules)
/** Mensagem de falha da carga dos combos — `null` quando os filtros carregaram. */
const filterOptionsLoadError = computed(() => dsAttendanceStore.filterOptionsLoadError)

const shiftStore = useShiftStore()
const { listShifts } = storeToRefs(shiftStore)

/** "Prazo (SLA)" não existe na aba Solicitações: lá não há SLA da fila (CA9). */
const sortOptions = computed(() =>
  onlyRequests.value ? sortOptionsData.filter((opt) => opt.value !== 'SLA') : sortOptionsData
)

/** Ordenação válida na aba atual (o `SLA` vindo da URL ou de outra aba não entra em Solicitações). */
function isSortAvailable(value: string): boolean {
  return !(onlyRequests.value && value === 'SLA')
}

const currentSortLabel = computed(() => {
  const selectedOption = sortOptions.value.find((opt) => opt.value === currentSort.value)
  return selectedOption ? selectedOption.label : 'Prazo (SLA)'
})

const scheduleIcon = defineAsyncComponent(() => import('@/assets/icons/scale.vue'))
const statusIcon = defineAsyncComponent(() => import('@/assets/icons/status.vue'))
const hospitalIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))
const shiftIcon = defineAsyncComponent(() => import('@/assets/icons/clock.vue'))

const isExportModalOpen = ref(false)
/** Aba de entrada do modal; a troca depois é dele, pela barra de abas. */
const exportMode = ref<'records' | 'attachments'>('records')

const canExportSheets = useCanExportSheets()

function openExportModal() {
  // Quem só extrai anexos (sem `attendance_sheet_exporting`) entra direto na aba dele.
  exportMode.value = canExportSheets.value ? 'records' : 'attachments'
  isExportModalOpen.value = true
}

/**
 * F4 — `attendance_extracting` em QUALQUER recurso de presença (escala, unidade
 * ou empresa): a exportação é global da fila, e exigir a permissão em todos os
 * recursos esconderia a aba de anexos de quem extrai a própria unidade.
 */
const canExportAttachments = computed(() =>
  [
    AuthorizationResourcesAndSubjects.DsAttendance,
    AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit,
    AuthorizationResourcesAndSubjects.DsAttendanceCompany
  ].some((resourceType) =>
    permissionsStore.checkAnyRessources({
      permission: AuthorizationPermissions.AttendanceExtracting,
      resourceType
    })
  )
)

/**
 * Enviar registro exige `attendance_emitting` — a mesma permissão que o FAB do
 * Detalhes do Plantão já consulta (`stationDetailsModal.vue`) para abrir ESTE
 * mesmo `AttendanceUploadModal`. A fila não tinha gate nenhum: quem só lia
 * plantão entrava na tela pela rota e clicava em "Novo registro" — hoje também o
 * perfil Extrator, que lê a fila por `attendance_reading`.
 */
const canEmitSheetRecord = useCanEmitAttendance()
const canAuditAttendance = useCanAuditAttendance()

/**
 * Mesma regra do passo 1 do modal: a política vigente da escala pode não aceitar
 * folha, e o envio morreria no backend depois do upload.
 *
 * Só vale COM escala no escopo. Esta tela busca a config sem `scheduleId` (a fila
 * é multi-escala), então hoje a condição nunca é verdadeira e o menu segue igual —
 * ela já fica de pé para quando a fila ganhar o seletor de escala do §8.2.
 */
const sheetBlockedReason = computed(() => {
  if (dsAttendanceStore.registrationSettingsScheduleId === null) return null
  return isSheetCategoryBlocked(dsAttendanceStore.registrationSettings)
    ? SHEET_BLOCKED_BY_APP_POLICY_REASON
    : null
})

const sendRecordTooltip = computed(() => {
  if (!canEmitSheetRecord.value) return 'Você não tem permissão para enviar registros.'
  return sheetBlockedReason.value ?? 'Novo registro'
})

/**
 * O recorte que o modal de exportação recebe: estruturalmente igual ao que `fetchData`
 * grava na store (`buildListParams` — objeto novo a cada avaliação). A premissa é que
 * todo filtro da fila dispara fetch imediato, então tela e modal andam juntos. Antes
 * eram cinco props soltas escolhidas a dedo, e a lista foi esquecida três vezes
 * (`shiftIds`, `activityType`, `manualReviewReason`); agora um filtro novo na fila
 * chega ao arquivo sem ninguém precisar lembrar dele.
 */
const exportQueueFilters = computed(() => buildListParams())

/**
 * Chips somente leitura do modal: um por filtro da fila, na ordem da tela — inclusive
 * os marcadores e as datas, que a ressalva do modal promete aplicar ao arquivo. O
 * `status` acompanha o gate do dropdown (`showStatusFilter`): na aba "Solicitações"
 * ele anunciaria um recorte que o backend não aplica.
 */
const exportActiveFilters = computed(() =>
  buildExportFilterChips({
    onlyRequests: onlyRequests.value,
    kind: kindFilter.value?.[0],
    showStatusFilter: showStatusFilter.value,
    statusLabels: selectedStatusObjects.value.map((s) => s.label),
    healthUnitLabels: selectedHealthUnitObjects.value.map((u) => u.label),
    scheduleLabels: selectedScheduleObjects.value.map((s) => s.label),
    shiftLabels: selectedShiftObjects.value.map((s) => s.label),
    activityTypeLabel: selectedActivityType.value
      ? ACTIVITY_TYPE_LABELS[selectedActivityType.value]
      : null,
    dateLabel: selectedDateLabel.value,
    uploadDateLabel: selectedUploadDateLabel.value,
    onlyDivergent: onlyDivergent.value,
    onlyAntecipe: onlyAntecipe.value,
    onlyInCash: onlyInCash.value,
    onlyStationChanged: onlyStationChanged.value,
    manualReviewReasonLabels: selectedManualReviewReasonObjects.value.map((r) => r.label),
    includeNoScheduleExpected: showNoScheduleExpected.value,
    slaStateLabels: showSlaStateFilter.value
      ? queueFilterState.value.slaStates.map((state) => CONTAINER_SLA_FILTER_STATE_LABELS[state])
      : [],
    stageLabels: showStageFilter.value
      ? queueFilterState.value.stages.map((stage) => CONTAINER_STAGE_LABELS[stage])
      : [],
    onlyOverdue: showOverdueFilter.value && queueFilterState.value.onlyOverdue
  })
)

/**
 * Cascata unidade -> escala, em memória.
 *
 * Antes, cada seleção chamava `scheduleStore.setSchedulesByHealthUnits`, que por dentro refazia
 * `getAllSchedules()` — ou seja, voltava ao `GET /schedules` que responde 403 ao receptor puro. O
 * `healthUnitId` que o endpoint de filtro devolve permite recortar aqui, sem nenhuma ida ao
 * servidor. Sem unidade escolhida a lista fica vazia, como antes (o combo de Escala já nasce
 * desabilitado nesse estado).
 */
const scheduleOptions = computed(() => {
  if (!selectedHealthUnitIds.value.length) return []

  const selected = new Set(selectedHealthUnitIds.value)
  return filterSchedules.value
    .filter((schedule) => selected.has(String(schedule.healthUnitId)))
    .map((schedule) => ({
      label: schedule.title,
      value: String(schedule.value)
    }))
})

const healthUnitOptions = computed(() =>
  filterHealthUnits.value.map((unit) => ({
    label: unit.title,
    value: String(unit.value)
  }))
)

const shiftOptions = computed(() =>
  listShifts.value.map((shift) => ({
    label: shift.name,
    value: String(shift.id)
  }))
)

const statusOptions = DS_ATTENDANCE_STATUS_OPTIONS

function handleSortSelect(value: string) {
  currentSort.value = value
  isSortModalOpen.value = false
  fetchData(true)
}

const selectedStatusObjects = computed(() =>
  selectedStatusKeys.value
    .map((key) => statusOptions.find((opt) => opt.value === key))
    .filter((s): s is { label: string; value: DSAttendanceStatus } => !!s)
)
const selectedScheduleObjects = computed(() =>
  selectedScheduleIds.value
    .map((id) => scheduleOptions.value.find((opt) => opt.value === id))
    .filter((s): s is { label: string; value: string } => !!s)
)
const selectedHealthUnitObjects = computed(() =>
  selectedHealthUnitIds.value
    .map((id) => healthUnitOptions.value.find((opt) => opt.value === id))
    .filter((u): u is { label: string; value: string } => !!u)
)
const selectedShiftObjects = computed(() =>
  selectedShiftIds.value
    .map((id) => shiftOptions.value.find((opt) => opt.value === id))
    .filter((s): s is { label: string; value: string } => !!s)
)

const selectedManualReviewReasonObjects = computed(() =>
  selectedManualReviewReasons.value
    .map((value) => manualReviewReasonOptions.find((opt) => opt.value === value))
    .filter((r): r is { label: string; value: string } => !!r)
)

const hasActiveFilters = computed(() => {
  const hasSchedules = selectedScheduleIds.value.length > 0
  const hasUnits = selectedHealthUnitIds.value.length > 0
  const hasDate = selectedDateRange.value.start !== null || selectedDateRange.value.end !== null
  const hasUploadDate =
    selectedUploadDateRange.value.start !== null || selectedUploadDateRange.value.end !== null
  const hasStatus = selectedStatusKeys.value.length > 0
  return [
    hasSchedules,
    hasUnits,
    hasDate,
    hasUploadDate,
    hasStatus,
    onlyDivergent.value,
    onlyAntecipe.value,
    onlyInCash.value,
    selectedManualReviewReasons.value.length > 0,
    selectedRegistrationModes.value.length > 0,
    selectedActivityType.value !== null,
    queueFilters.activeCount.value > 0 && !onlyRequests.value
  ].some(Boolean)
})

const unifiedFiltersList = computed<FilterTag[]>(() => {
  const tags: FilterTag[] = []

  // O chip só aparece quando o filtro existe (I5): na aba "Solicitações" ele anunciaria um
  // recorte que o backend não aplica.
  if (showStatusFilter.value) {
    selectedStatusObjects.value.forEach((s) => {
      tags.push({ id: `status-${s.value}`, label: s.label, type: 'status', value: s.value })
    })
  }

  if (selectedDateLabel.value) {
    tags.push({ id: 'date', label: selectedDateLabel.value, type: 'date' })
  }

  if (selectedUploadDateLabel.value) {
    tags.push({
      id: 'uploadDate',
      label: `Upload: ${selectedUploadDateLabel.value}`,
      type: 'uploadDate'
    })
  }

  selectedHealthUnitObjects.value.forEach((u) => {
    tags.push({ id: `unit-${u.value}`, label: u.label, type: 'unit', value: u.value })
  })

  selectedScheduleObjects.value.forEach((s) => {
    tags.push({ id: `schedule-${s.value}`, label: s.label, type: 'schedule', value: s.value })
  })

  selectedShiftObjects.value.forEach((s) => {
    tags.push({ id: `shift-${s.value}`, label: s.label, type: 'shift', value: s.value })
  })

  if (onlyDivergent.value) {
    tags.push({ id: 'divergent', label: 'Somente com divergência', type: 'divergent' })
  }

  if (onlyAntecipe.value) {
    tags.push({ id: 'antecipe', label: 'Com Antecipe', type: 'antecipe' })
  }

  if (onlyInCash.value) {
    tags.push({ id: 'inCash', label: 'Com À vista', type: 'inCash' })
  }

  if (selectedActivityType.value) {
    tags.push({
      id: 'activityType',
      label: `Tipo: ${ACTIVITY_TYPE_LABELS[selectedActivityType.value]}`,
      type: 'activityType',
      value: selectedActivityType.value
    })
  }

  selectedManualReviewReasonObjects.value.forEach((r) => {
    tags.push({
      id: `manualReviewReason-${r.value}`,
      label: `Revisão: ${r.label}`,
      type: 'manualReviewReason',
      value: r.value
    })
  })

  selectedRegistrationModes.value.forEach((mode) => {
    tags.push({
      id: `registrationMode-${mode}`,
      label: `Modalidade: ${REGISTRATION_MODE_LABELS[mode]}`,
      type: 'registrationMode',
      value: mode
    })
  })

  tags.push(...queueSlaFilterTags())

  return tags
})

/**
 * Filtros que não são "lista de ids": os toggles booleanos e o multi-select de motivo.
 * Separados de `handleRemoveGenericFilter` para manter cada um abaixo do teto de complexidade
 * do lint — o switch cresce a cada filtro novo da fila.
 */
function removeNonListFilter(filter: FilterTag): boolean {
  switch (filter.type) {
    case 'divergent':
      onlyDivergent.value = false
      break
    case 'antecipe':
      onlyAntecipe.value = false
      break
    case 'inCash':
      onlyInCash.value = false
      break
    case 'manualReviewReason':
      selectedManualReviewReasons.value = selectedManualReviewReasons.value.filter(
        (r) => r !== filter.value
      )
      break
    case 'registrationMode':
      selectedRegistrationModes.value = selectedRegistrationModes.value.filter(
        (m) => m !== filter.value
      )
      break
    case 'activityType':
      selectedActivityType.value = null
      break
    default:
      return false
  }
  fetchData(true)
  return true
}

const handleRemoveGenericFilter = (filter: FilterTag) => {
  if (removeQueueSlaFilter(filter)) return
  if (removeNonListFilter(filter)) return

  switch (filter.type) {
    case 'status':
      removeStatus(filter.value)
      break
    case 'date':
      clearDate()
      break
    case 'uploadDate':
      clearUploadDate()
      break
    case 'schedule':
      removeSchedule(filter.value)
      break
    case 'unit':
      removeHealthUnit(filter.value)
      break
    case 'shift':
      removeShift(filter.value)
      break
  }
}

/**
 * "Limpar filtros" NÃO mexe na aba de categoria: a aba é o recorte da fila que o auditor
 * escolheu trabalhar, não um filtro que ele acumulou — trocá-la é um clique no controle.
 */
function clearAllFilters() {
  selectedHealthUnitIds.value = []
  selectedScheduleIds.value = []
  selectedShiftIds.value = []

  shiftStore.setShiftsBySchedules(null)

  selectedDateRange.value = { start: null, end: null }
  selectedDateLabel.value = null

  selectedUploadDateRange.value = { start: null, end: null }
  selectedUploadDateLabel.value = null

  currentPage.value = 1
  selectedStatusKeys.value = []
  selectedActivityType.value = null
  onlyDivergent.value = false
  onlyAntecipe.value = false
  onlyInCash.value = false
  selectedManualReviewReasons.value = []
  selectedRegistrationModes.value = []
  queueFilters.clear()
  closeAllFilters()
  fetchData()
}

function toggleFilter(name: string) {
  if (name !== 'date') dateFilterRef.value?.close?.()
  if (name !== 'uploadDate') uploadDateFilterRef.value?.close?.()

  openFilterName.value = openFilterName.value === name ? null : name
}

function closeAllFilters() {
  openFilterName.value = null
  isSendRecordMenuOpen.value = false
  dateFilterRef.value?.close()
  uploadDateFilterRef.value?.close()
}

const changePage = (page: number) => {
  currentPage.value = page
  fetchData()
}

const handleDetails = async (attendanceId: number | string) => {
  const item = findQueueItem(attendanceId)
  if (!item) return

  const routeName = routeNameForStatus(item.status)
  if (!routeName) return

  try {
    const details = await Network.dsAttendance.getById(attendanceId)
    router.push({
      name: routeName,
      params: { attendanceId: String(attendanceId) },
      // Eixo 4 — os filtros da fila viajam na query do registro: é com eles que o
      // `useNextContainer` pede "o próximo" sem o auditor voltar à lista.
      query: { ...dsAttendanceStore.listQueryParams },
      state: { details: JSON.parse(JSON.stringify(details)), status: item.status }
    })
  } catch (error) {
    console.error('Erro ao buscar detalhes do registro:', error)
    notifyError(error, 'Não foi possível abrir os detalhes do registro de presença.')
  }
}

function handleStatusSelect(value: string) {
  const status = value as DSAttendanceStatus
  const currentKeys = [...selectedStatusKeys.value]
  const index = currentKeys.indexOf(status)
  if (index > -1) currentKeys.splice(index, 1)
  else currentKeys.push(status)
  selectedStatusKeys.value = currentKeys
  currentPage.value = 1
  fetchData(true)
}

function handleScheduleSelect(value: string) {
  const currentIds = [...selectedScheduleIds.value]
  const index = currentIds.indexOf(value)
  if (index > -1) currentIds.splice(index, 1)
  else currentIds.push(value)
  selectedScheduleIds.value = currentIds
  selectedShiftIds.value = []
  shiftStore.setShiftsBySchedules(currentIds.length ? currentIds.map(Number) : null)
  currentPage.value = 1
  fetchData(true)
}

function handleHealthUnitSelect(value: string) {
  const currentIds = [...selectedHealthUnitIds.value]
  const index = currentIds.indexOf(value)
  if (index > -1) currentIds.splice(index, 1)
  else currentIds.push(value)
  selectedHealthUnitIds.value = currentIds
  selectedScheduleIds.value = []
  selectedShiftIds.value = []
  shiftStore.setShiftsBySchedules(null)
  currentPage.value = 1
  fetchData(true)
}

function handleShiftSelect(value: string) {
  const currentIds = [...selectedShiftIds.value]
  const index = currentIds.indexOf(value)
  if (index > -1) currentIds.splice(index, 1)
  else currentIds.push(value)
  selectedShiftIds.value = currentIds
  currentPage.value = 1
  fetchData(true)
}

const DATE_FORMAT = 'dd/MM/yyyy'

function handleDateFilter(payload: DateRange, shouldFetch = true) {
  selectedDateRange.value = payload
  if (payload.start && payload.end) {
    const formattedStart = format(payload.start, DATE_FORMAT, { locale: ptBR })
    const formattedEnd = format(payload.end, DATE_FORMAT, { locale: ptBR })
    selectedDateLabel.value =
      formattedStart === formattedEnd ? formattedStart : `${formattedStart} - ${formattedEnd}`
  } else {
    selectedDateLabel.value = null
  }

  if (shouldFetch) {
    currentPage.value = 1
    fetchData()
  }
}

function handleUploadDateFilter(payload: DateRange, shouldFetch = true) {
  selectedUploadDateRange.value = payload
  if (payload.start && payload.end) {
    const formattedStart = format(payload.start, DATE_FORMAT, { locale: ptBR })
    const formattedEnd = format(payload.end, DATE_FORMAT, { locale: ptBR })
    selectedUploadDateLabel.value =
      formattedStart === formattedEnd ? formattedStart : `${formattedStart} - ${formattedEnd}`
  } else {
    selectedUploadDateLabel.value = null
  }

  if (shouldFetch) {
    currentPage.value = 1
    fetchData()
  }
}

function removeStatus(value: string) {
  selectedStatusKeys.value = selectedStatusKeys.value.filter((k) => k !== value)
  fetchData()
}
function removeSchedule(value: string) {
  selectedScheduleIds.value = selectedScheduleIds.value.filter((k) => k !== value)
  fetchData()
}
function removeHealthUnit(value: string) {
  selectedHealthUnitIds.value = selectedHealthUnitIds.value.filter((k) => k !== value)
  selectedScheduleIds.value = []
  selectedShiftIds.value = []
  shiftStore.setShiftsBySchedules(null)
  fetchData()
}

function removeShift(value: string) {
  selectedShiftIds.value = selectedShiftIds.value.filter((k) => k !== value)
  fetchData()
}
function clearDate() {
  selectedDateRange.value = { start: null, end: null }
  selectedDateLabel.value = null
  fetchData()
}
function clearUploadDate() {
  selectedUploadDateRange.value = { start: null, end: null }
  selectedUploadDateLabel.value = null
  fetchData()
}

// const onSearchByTabIndex = () => {
//   currentPage.value = 1
//   fetchData()
// }

function buildDateRangeQuery(range: DateRange, startKey: string, endKey: string) {
  const result: Record<string, string> = {}
  if (range.start) result[startKey] = format(range.start, 'yyyy-MM-dd')
  if (range.end) result[endKey] = format(range.end, 'yyyy-MM-dd')
  return result
}

/** Filtros de múltipla escolha viram CSV na URL; vazios não entram. */
function buildListFiltersQuery(): Record<string, string> {
  const query: Record<string, string> = {}
  if (selectedStatusKeys.value.length) query.status = selectedStatusKeys.value.join(',')
  if (selectedScheduleIds.value.length) query.scheduleIds = selectedScheduleIds.value.join(',')
  if (selectedHealthUnitIds.value.length)
    query.healthUnitIds = selectedHealthUnitIds.value.join(',')
  if (selectedShiftIds.value.length) query.shiftIds = selectedShiftIds.value.join(',')
  if (selectedManualReviewReasons.value.length)
    query.manualReviewReason = selectedManualReviewReasons.value.join(',')
  if (selectedRegistrationModes.value.length)
    query.registrationModes = selectedRegistrationModes.value.join(',')
  // F2 — a aba e o tipo viajam na URL: um auditor manda o link da fila de Atividade ao outro
  // (e a tela de sucesso do envio volta para cá com `kind=ATIVIDADE`).
  if (kindFilter.value?.length) query.kind = kindFilter.value.join(',')
  // F3 — a aba "Solicitações" também viaja na URL, pelo mesmo motivo: o link da fila é como
  // um auditor chama o outro para a mesma tela.
  if (onlyRequests.value) query.onlyRequests = 'true'
  if (selectedActivityType.value) query.activityType = selectedActivityType.value
  return query
}

/**
 * Toggles booleanos só entram na URL quando ligados: `=false` diria ao backend
 * "somente SEM ...", que não é o que o usuário pediu ao desligar o switch.
 */
const updateUrlWithFilters = () => {
  const query: Record<string, string> = {}
  // `tab=` só faz sentido no shell de abas; na rota própria da fila seria um param morto.
  if (!flags.enabledApprovalsHub) query.tab = 'attendance'
  Object.assign(query, buildListFiltersQuery())
  Object.assign(query, buildToggleFiltersQuery())
  // Spec 2026-10-01 §7.2 — visão/agrupamento/recorte SLA viajam no link (CA1).
  if (!onlyRequests.value) Object.assign(query, queueFilters.toUrl())

  Object.assign(query, buildDateRangeQuery(selectedDateRange.value, 'startDate', 'endDate'))
  Object.assign(
    query,
    buildDateRangeQuery(selectedUploadDateRange.value, 'uploadStartDate', 'uploadEndDate')
  )

  if (currentPage.value > 1) query.page = String(currentPage.value)
  if (currentSort.value && currentSort.value !== defaultSort.value) query.sort = currentSort.value

  dsAttendanceStore.listQueryParams = { ...query }
  router.push({ query })
}

function parseQueryString(value: unknown): string | null {
  return value ? String(value) : null
}

function parseArrayFromQuery(value: unknown): string[] {
  return value ? String(value).split(',') : []
}

function parseDateRangeFromQuery(startParam: string | null, endParam: string | null): DateRange {
  return {
    start: startParam ? parseISO(startParam) : null,
    end: endParam ? parseISO(endParam) : null
  }
}

function parseStatusKeysFromQuery(
  rawValue: string | (string | null)[] | null | undefined
): DSAttendanceStatus[] {
  const validValues = Object.values(DSAttendanceStatus) as string[]
  const rawStatus = rawValue ? String(rawValue).split(',') : []
  const validStatus = rawStatus.filter((key) => validValues.includes(key)) as DSAttendanceStatus[]
  return validStatus
}

/** Toggles ligados viram `=true` na URL; desligados não entram (o inverso não existe). */
function buildToggleFiltersQuery(): Record<string, string> {
  const query: Record<string, string> = {}
  if (onlyDivergent.value) query.hasDivergentEntries = 'true'
  if (onlyAntecipe.value) query.hasAntecipe = 'true'
  if (onlyInCash.value) query.hasInCash = 'true'
  if (onlyStationChanged.value) query.stationChangedAfterAudit = 'true'
  return query
}

/**
 * F2 — aba e tipo vindos da URL. Valor desconhecido é ignorado (volta a "Todos") em vez de
 * mandar lixo ao backend.
 */
function setKindFiltersFromUrl(query: Record<string, unknown>) {
  const validKinds = Object.values(ContainerKind) as string[]
  const parsedKinds = parseArrayFromQuery(query.kind).filter((k) =>
    validKinds.includes(k)
  ) as ContainerKind[]
  kindFilter.value = parsedKinds.length ? parsedKinds : null
  onlyRequests.value = parseQueryString(query.onlyRequests) === 'true'

  const rawType = parseQueryString(query.activityType)
  const validTypes = Object.values(ActivityType) as string[]
  selectedActivityType.value =
    rawType && validTypes.includes(rawType) ? (rawType as ActivityType) : null
}

/** Toggles booleanos da URL (`=true` liga; qualquer outra coisa desliga). */
function setToggleFiltersFromUrl(query: Record<string, unknown>) {
  onlyDivergent.value = parseQueryString(query.hasDivergentEntries) === 'true'
  onlyAntecipe.value = parseQueryString(query.hasAntecipe) === 'true'
  onlyInCash.value = parseQueryString(query.hasInCash) === 'true'
  onlyStationChanged.value = parseQueryString(query.stationChangedAfterAudit) === 'true'
}

const setFiltersFromUrl = () => {
  const query = route.query

  selectedHealthUnitIds.value = parseArrayFromQuery(query.healthUnitIds)
  selectedScheduleIds.value = parseArrayFromQuery(query.scheduleIds)
  selectedShiftIds.value = parseArrayFromQuery(query.shiftIds)
  selectedManualReviewReasons.value = parseArrayFromQuery(query.manualReviewReason)
  // Sem a flag o valor da URL é ignorado: um filtro que o backend descarta não pode ficar
  // "aceso" num chip que não filtra nada.
  selectedRegistrationModes.value = showRegistrationModeFilter.value
    ? (parseArrayFromQuery(query.registrationModes).filter((m) =>
        (REGISTRATION_MODES as readonly string[]).includes(m)
      ) as RegistrationMode[])
    : []
  setKindFiltersFromUrl(query)
  setToggleFiltersFromUrl(query)
  // G2-D7 — sem `status` na URL = todas as etapas (antes: PENDING_AUDIT). Link antigo com
  // `status=` explícito continua valendo e cruza com as etapas. A aba Solicitações mantém o
  // padrão de antes (F17/CA9) — depende de `onlyRequests`, por isso vem depois da aba.
  const parsedStatus = parseStatusKeysFromQuery(query.status)
  selectedStatusKeys.value =
    parsedStatus.length || !onlyRequests.value ? parsedStatus : [DSAttendanceStatus.PENDING_AUDIT]
  queueFilters.hydrate(query as Record<string, unknown>)
  currentPage.value = query.page ? Number(query.page) : 1
  const urlSort = query.sort ? String(query.sort) : null
  currentSort.value = urlSort && isSortAvailable(urlSort) ? urlSort : defaultSort.value

  // A lista de escalas não precisa ser repovoada aqui: `scheduleOptions` deriva de
  // `selectedHealthUnitIds`, então restaurar o filtro pela URL já basta.
  if (selectedScheduleIds.value.length) {
    shiftStore.setShiftsBySchedules(selectedScheduleIds.value.map(Number))
  }

  selectedDateRange.value = parseDateRangeFromQuery(
    parseQueryString(query.startDate),
    parseQueryString(query.endDate)
  )
  selectedUploadDateRange.value = parseDateRangeFromQuery(
    parseQueryString(query.uploadStartDate),
    parseQueryString(query.uploadEndDate)
  )

  handleDateFilter(selectedDateRange.value, false)
  handleUploadDateFilter(selectedUploadDateRange.value, false)
}

const toLocalDate = (date: Date | null) => (date ? toDateOnly(date) : null)

/** O recorte da fila SEM paginação, como `ListContainersByCompanyParams` o tipa. */
type QueueFilters = Omit<ListContainersByCompanyParams, 'page' | 'limit'>

/**
 * O recorte da fila a partir das refs locais — a ÚNICA fonte tanto do que a listagem
 * manda ao `by-company` quanto do que o modal de exportação recebe em `queueFilters`.
 * Um filtro novo na fila entra aqui uma vez e chega às duas pontas por construção; é
 * isto que aposenta a lista de props soltas que foi esquecida três vezes.
 */
function buildQueueFilters(): QueueFilters {
  const base: QueueFilters = {
    // Cru, mesmo na aba "Solicitações": ali a listagem ignora o status no backend e o modal
    // bloqueia o export por `onlyRequests`; manter o valor preserva a contagem por espécie
    // (`counts-by-kind`), que o usa. O chip de Status é que segue o gate `showStatusFilter`.
    status: selectedStatusKeys.value,
    healthUnitIds: selectedHealthUnitIds.value.map(Number),
    scheduleIds: selectedScheduleIds.value.map(Number),
    shiftIds: selectedShiftIds.value.map(Number),
    startDate: toLocalDate(selectedDateRange.value.start),
    endDate: toLocalDate(selectedDateRange.value.end),
    uploadStartDate: toLocalDate(selectedUploadDateRange.value.start),
    uploadEndDate: toLocalDate(selectedUploadDateRange.value.end),
    hasDivergentEntries: onlyDivergent.value,
    hasAntecipe: onlyAntecipe.value,
    hasInCash: onlyInCash.value,
    stationChangedAfterAudit: onlyStationChanged.value,
    includeNoScheduleExpected: showNoScheduleExpected.value,
    manualReviewReason: selectedManualReviewReasons.value,
    registrationModes: showRegistrationModeFilter.value ? selectedRegistrationModes.value : [],
    // F2 — `undefined` (e não `null`) para a chave nem entrar na query: ausente = "Todos".
    kind: kindFilter.value?.[0] ?? undefined,
    activityType: selectedActivityType.value ?? undefined,
    // `undefined` quando desligado: `onlyRequests=false` pediria ao backend "tudo MENOS
    // solicitação", que não é o recorte de nenhuma aba.
    onlyRequests: onlyRequests.value || undefined,
    orderBy: currentSort.value as DSAttendanceContainerOrderBy
  }
  // Ponto ÚNICO do recorte SLA: lista, Kanban, contadores, export e "próximo" leem daqui.
  return queueFilters.toParams(base, {
    // F5 — relógio do servidor (o `closedSince` não sai do relógio do cliente).
    nowMs: queueNowMs(),
    hasExplicitHistory:
      selectedStatusKeys.value.length > 0 ||
      selectedDateRange.value.start !== null ||
      selectedDateRange.value.end !== null
  })
}

/**
 * Os parâmetros completos da listagem: o recorte acima, a página corrente e o `limit`
 * da store. É o objeto que `fetchData` grava na store e, estruturalmente igual (objeto
 * novo a cada avaliação), o que o modal de exportação recebe — o modal descarta
 * `page`/`limit`/`orderBy` por conta própria. Explícito, sem spread da store: só o
 * que a fila decide entra no recorte (o `overdue: false` do default, por exemplo, não).
 */
function buildListParams(): ListContainersByCompanyParams {
  return {
    ...buildQueueFilters(),
    page: currentPage.value,
    limit: dsAttendanceStore.containerFilters.limit
  }
}

/**
 * DEV-QA (PR #2839) — trocar de aba (Todos/Folha/Atividade) e de filtro em sequência rápida
 * dispara buscas concorrentes; a mais antiga, resolvendo por último, sobrescrevia a lista.
 * Cada chamada ganha um número e só a MAIS RECENTE mapeia a lista e encerra o loading (a
 * store aplica a mesma trava ao próprio estado).
 */
let listFetchSeq = 0

const fetchData = async (resetPage = false) => {
  const seq = ++listFetchSeq
  if (isRequestsV2.value) {
    // A aba nova busca sozinha e é dona da URL; aqui só a contagem das outras pílulas.
    dsAttendanceStore.containerFilters = buildListParams()
    dsAttendanceStore.fetchCountsByKind()
    loading.value = false
    return
  }
  if (resetPage) currentPage.value = 1
  // A seleção da Tabela vale para a página/recorte em que foi feita (Review Focus 5).
  selectedTableIds.value = []
  lastFetchAt = Date.now()

  if (isKanbanActive.value) {
    // Kanban carrega por coluna (4 colunas + sla-counters): a lista paginada não é buscada.
    updateUrlWithFilters()
    dsAttendanceStore.containerFilters = buildListParams()
    dsAttendanceStore.fetchCountsByKind()
    queueParams.value = buildQueueFilters()
    kanbanReloadToken.value += 1
    loading.value = false
    return
  }

  loading.value = true
  updateUrlWithFilters()

  try {
    // 1. Sincroniza os filtros com a Store — pela MESMA função que alimenta o modal de
    //    exportação (`exportQueueFilters`): a tela e o arquivo leem o mesmo objeto.
    dsAttendanceStore.containerFilters = buildListParams()
    queueParams.value = buildQueueFilters()

    // 2. Busca os dados. A contagem por espécie roda junto e NUNCA derruba a fila: a action
    // engole a falha e zera `counts`, que só apaga o segmented control.
    await dsAttendanceStore.fetchContainersByCompany()
    if (seq !== listFetchSeq) return
    dsAttendanceStore.fetchCountsByKind()

    // 3. Mapeia com segurança (Resolvendo o erro de 'possibly undefined')
    totalItems.value = dsAttendanceStore.totalContainers
    list.value = dsAttendanceStore.containers.map(toAttendanceListItem)
  } catch (error) {
    if (seq !== listFetchSeq) return
    // O estado de erro visível (mensagem + retry) vem de
    // `dsAttendanceStore.containersLoadError`, preenchido pela action ao relançar.
    console.error('Erro ao buscar os registros de presença:', error)
    list.value = []
    totalItems.value = 0
  } finally {
    if (seq === listFetchSeq) loading.value = false
  }
}

const handleOnSave = () => {
  fetchData()
}

/** Nova tentativa dos combos, sem recarregar a fila (as duas cargas são independentes). */
function reloadFilterOptions() {
  return dsAttendanceStore.fetchFilterOptions()
}

onMounted(async () => {
  window.addEventListener('focus', onWindowFocus)
  await Promise.all([
    dsAttendanceStore.fetchFilterOptions(),
    // F2 — decide se o botão vira o dropdown "Enviar registro". Nunca lança: o default
    // seguro do store mantém a tela idêntica à de hoje.
    dsAttendanceStore.fetchRegistrationSettings()
  ])

  const hasFilterParams = Object.keys(route.query).some((key) => key !== 'tab')
  if (!hasFilterParams && dsAttendanceStore.listQueryParams) {
    await router.replace({ query: dsAttendanceStore.listQueryParams })
  }

  setFiltersFromUrl()
  fetchData()
})
</script>
